import { CONFIG, PLANETS, snapTolerance } from './config';
import type { PlanetId, PlacementTarget, Point } from './types';

export interface SceneLayout {
  width: number;
  height: number;
  sunCentre: Point;
  sunDiameter: number;
  /** Centre of each orbit slot, indexed by planet.orbitIndex. */
  slotCentres: Record<PlanetId, Point>;
  slotDiameters: Record<PlanetId, number>;
  /** Orbit arc radii (px from sun centre) per planet. */
  orbitRadii: Record<PlanetId, number>;
  snapTolerance: number;
}

/**
 * Compute the scene layout for a given viewport. Sun is anchored to the left.
 * Slots are arranged left-to-right in astronomical order. The layout is purely
 * geometric so it can be unit-tested without DOM access.
 */
export function computeLayout(viewportW: number, viewportH: number): SceneLayout {
  // Sun sizing accounts for both dimensions so portrait phones do not let the
  // Sun consume all horizontal real estate (which would force orbit overlap).
  const sunDiameter = clamp(
    Math.min(viewportH * 0.42, viewportW * 0.32, 240),
    140,
    280,
  );
  const sceneHeight = viewportH;
  const sceneWidth = viewportW;

  // Reserve space for the dock + caption at the bottom.
  const reservedBottom = Math.max(180, sceneHeight * 0.28);
  const playTop = 24;
  const playHeight = Math.max(160, sceneHeight - reservedBottom - playTop);
  const playCentreY = playTop + playHeight / 2;

  const sunCentre = { x: Math.max(sunDiameter / 2 + 16, sceneWidth * 0.13), y: playCentreY };

  const sunRightEdge = sunCentre.x + sunDiameter / 2;
  const rightMargin = 40;
  const availableWidth = sceneWidth - sunRightEdge - rightMargin;
  const slotCount = PLANETS.length;

  const snap = snapTolerance(viewportW, viewportH);

  const slotCentres = {} as Record<PlanetId, Point>;
  const slotDiameters = {} as Record<PlanetId, number>;
  const orbitRadii = {} as Record<PlanetId, number>;

  // Portrait viewports have no room for four silhouettes in a horizontal row;
  // stagger Y so adjacent silhouettes never visually overlap. Landscape keeps
  // a single horizontal row because spacing is wide enough.
  // 56px floor keeps vertical separation at or above the silhouette size
  // floor on very narrow viewports (slot pitch alone would let adjacent
  // boxes collide). Capped at 96 so extreme portrait never over-staggers.
  const portrait = sceneHeight > sceneWidth;
  const stagger = portrait ? Math.max(56, Math.min(playHeight * 0.18, 96)) : 0;

  for (const planet of PLANETS) {
    const fraction = (planet.orbitIndex + 1) / (slotCount + 1);
    const x = sunRightEdge + availableWidth * fraction;
    const y = playCentreY + (planet.orbitIndex - (slotCount - 1) / 2) * stagger;
    slotCentres[planet.id] = { x, y };
    // Cap the silhouette diameter by available horizontal slot width so
    // adjacent silhouettes never overlap on narrow viewports. The planet
    // touch target itself remains >=96px via the .planet wrapper; only the
    // silhouette (a drop target, not a touch target) shrinks.
    const slotWidth = availableWidth / (slotCount + 1);
    // Silhouette floor 56px (not 48) so portrait targets stay matchable to
    // their docked planet; capped by slot width so they never overlap.
    const maxDiameter = Math.max(56, Math.min(planet.baseDiameter, slotWidth * 0.7));
    slotDiameters[planet.id] = maxDiameter;
    orbitRadii[planet.id] = Math.hypot(x - sunCentre.x, y - sunCentre.y);
  }

  return {
    width: sceneWidth,
    height: sceneHeight,
    sunCentre,
    sunDiameter,
    slotCentres,
    slotDiameters,
    orbitRadii,
    snapTolerance: snap,
  };
}

/**
 * Build the placement target list used by dragOrTapToPlace. Expands each
 * silhouette's bounding box by `margin` and adds a snapTolerance radius around
 * the centre. Both criteria are accepted (per PRP §Placement validation).
 */
export function buildTargets(layout: SceneLayout): PlacementTarget[] {
  return PLANETS.map((p) => {
    const centre = layout.slotCentres[p.id];
    const diameter = layout.slotDiameters[p.id];
    return {
      id: p.id,
      centre,
      margin: CONFIG.targetMarginPx + diameter * 0.25,
      snapTolerance: layout.snapTolerance,
    };
  });
}

/**
 * Validation function shared by drag and tap-tap (PRP AC-07). A release point
 * matches a target if either:
 *   (a) the release lies inside the target's expanded bounding box, OR
 *   (b) the release lies within snapTolerance of the target centre.
 * Any silhouette accepts only its matching planet (PRP §Placement validation).
 */
export function isReleaseOnTarget(
  target: PlacementTarget,
  release: Point,
  draggableId: PlanetId,
): boolean {
  if (target.id !== draggableId) return false;
  const dx = release.x - target.centre.x;
  const dy = release.y - target.centre.y;
  const dist = Math.hypot(dx, dy);
  if (dist <= target.snapTolerance) return true;
  const halfBox = target.margin;
  return Math.abs(dx) <= halfBox && Math.abs(dy) <= halfBox;
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(v, max));
}
